import * as XLSX from 'xlsx'
import { round2 } from '../lib/ciNormalise'
import { extractTextFromPDF, fileToBase64 } from './commercialInvoicePdf'
import { parseInvoiceWithClaude, parseSingleFieldWithClaude } from './claudeInvoiceParser'
import { parseUsingTemplate, getTemplatesForCustomer, getTemplateById, matchTemplates, matchTemplateBySignature } from './invoiceTemplates'
import type { InvoiceTemplate } from './invoiceTemplates'
import type { InvoiceItem, InvoiceParsed, InvoicePivotItem, FieldMappings } from './claudeInvoiceParser'
import { findInvalidHeaderFields } from './invoiceFieldValidation'

// Thrown instead of returning a result when 2+ of a customer's templates
// scored a close match against this file -- the caller must ask staff to
// pick one (or choose AI) and re-call parseCommercialInvoiceFile with
// { forceTemplateId } or { forceAI: true } to actually complete the parse.
export class AmbiguousTemplateMatch extends Error {
  candidates: InvoiceTemplate[]
  constructor(candidates: InvoiceTemplate[]) {
    super(`${candidates.length} templates matched this file -- staff must pick one`)
    this.name = 'AmbiguousTemplateMatch'
    this.candidates = candidates
  }
}

export function getFileType(file: File): 'pdf' | 'excel' | 'unsupported' {
  const name = file.name.toLowerCase()
  if (name.endsWith('.pdf')) return 'pdf'
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) return 'excel'
  return 'unsupported'
}

// Z42.2: a shipper's combined CI/PL workbook is frequently 3+ sheets -- the
// Invoice, the Packing List, and one or more reference sheets that are never
// part of the parse (most commonly a customs "申报要素" / declaration-elements
// sheet). Per Gary: only ever read the Invoice + Packing List, in whichever
// of the first few sheet slots they actually land -- never the declaration
// sheet, never anything past it.
export type SheetRole = 'invoice' | 'packingList' | 'unknown'

export interface SheetInfo {
  name: string
  role: SheetRole
  // Row index into the CONCATENATED rows[][] grid where this sheet's own
  // row 0 begins -- e.g. a 36-row Invoice sheet followed by a Packing List
  // gives the Packing List startRow: 36. field_mappings row/col positions
  // are always absolute indices into this concatenated grid, so a template
  // field can point into either sheet with no schema change at all.
  startRow: number
  rowCount: number
  fileName?: string
}

const DECLARATION_ELEMENTS_SHEET_PATTERN = /申报要素|declaration\s*element/i
const INVOICE_SHEET_NAME_HINT = /invoice|发票/i
const PACKING_LIST_SHEET_NAME_HINT = /packing|箱单|装箱/i

function sheetHasData(rows: unknown[][]): boolean {
  return rows.some(row => row?.some(cell => cell !== undefined && cell !== null && String(cell).trim() !== ''))
}

// Name hint wins when present (staff/shippers usually do label their sheets
// sensibly); otherwise falls back to content -- an Invoice sheet has price
// columns ("Unit Value", "Unit Price") and/or a "COMMERCIAL INVOICE" /
// "INVOICE NO" banner, a Packing List has carton/weight columns and no price
// column.
function detectSheetRole(name: string, rows: unknown[][]): SheetRole {
  const isInvoiceName = INVOICE_SHEET_NAME_HINT.test(name)
  const isPackingName = PACKING_LIST_SHEET_NAME_HINT.test(name)
  if (isInvoiceName && !isPackingName) return 'invoice'
  if (isPackingName && !isInvoiceName) return 'packingList'

  const text = rows.slice(0, 20).flatMap(row => (row || []).map(cell => String(cell ?? '').toLowerCase())).join(' | ')
  const hasPriceColumn = /unit\s*value|unit\s*price/.test(text)
  const hasInvoiceBanner = /commercial invoice|invoice no/.test(text)
  const hasPackingBanner = /packing list/.test(text)
  const hasWeightColumns = /gross weight|net weight|g\.w\.?|n\.w\.?|carton/.test(text)

  if (hasInvoiceBanner || hasPriceColumn) return 'invoice'
  if (hasPackingBanner || hasWeightColumns) return 'packingList'
  return 'unknown'
}

function readWorkbookFromFile(file: File, opts?: { raw?: boolean }): Promise<XLSX.WorkBook> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      try {
        resolve(XLSX.read(reader.result as ArrayBuffer, { type: 'array', raw: opts?.raw ?? false }))
      } catch (err) {
        reject(err)
      }
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsArrayBuffer(file)
  })
}

const ROLE_ORDER: Record<SheetRole, number> = { invoice: 0, packingList: 1, unknown: 2 }

// Filters out any declaration-elements sheet (wherever it sits) and any
// sheet with no actual data, keeps only the first 2 of what's left (per
// Gary, the Invoice and the Packing List, never a 3rd reference sheet), then
// sorts Invoice-role before Packing-List-role -- Z42.2.2: a template's
// field_mappings row/col positions must land in the same place on every
// future upload, so concatenation order has to be canonical (invoice, then
// packing list) regardless of which physical sheet slot each one happens to
// occupy in a given file.
function selectRelevantSheets(workbook: XLSX.WorkBook): { name: string; rows: unknown[][]; role: SheetRole }[] {
  const candidates = workbook.SheetNames.filter(name => !DECLARATION_ELEMENTS_SHEET_PATTERN.test(name))
  const withRows = candidates.map(name => ({
    name,
    rows: XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1 }) as unknown[][],
  }))
  const relevant = withRows.filter(sheet => sheetHasData(sheet.rows)).slice(0, 2)
  const withRole = relevant.map(sheet => ({ ...sheet, role: detectSheetRole(sheet.name, sheet.rows) }))
  return withRole
    .map((sheet, index) => ({ sheet, index }))
    .sort((a, b) => ROLE_ORDER[a.sheet.role] - ROLE_ORDER[b.sheet.role] || a.index - b.index)
    .map(({ sheet }) => sheet)
}

// Reads a (possibly multi-sheet) CI/PL workbook as a single concatenated
// rows[][] grid, tagged with which stretch of rows came from which sheet
// (and that sheet's detected Invoice/Packing-List role). Concatenating
// rather than keeping sheets separate means every existing field_mappings/
// ItemTableSpec row+col position, anchor search, and structure-signature
// check keeps working completely unchanged -- a template field mapped into
// the Packing List sheet is just a row number past the end of the Invoice
// sheet's rows.
export async function readExcelRowsMultiSheet(file: File, opts?: { raw?: boolean }): Promise<{ rows: unknown[][]; sheets: SheetInfo[] }> {
  const workbook = await readWorkbookFromFile(file, opts)
  const selected = selectRelevantSheets(workbook)
  const rows: unknown[][] = []
  const sheets: SheetInfo[] = []
  for (const sheet of selected) {
    sheets.push({ name: sheet.name, role: sheet.role, startRow: rows.length, rowCount: sheet.rows.length, fileName: file.name })
    rows.push(...sheet.rows)
  }
  return { rows, sheets }
}

// Z42.2.2: lightweight sheet-name-only read for the Template Setup Wizard's
// "which sheet is Invoice / which is Packing List?" picker -- lets staff
// confirm before the wizard reads full row data or spends an AI call.
export async function listWorkbookSheetNames(file: File): Promise<string[]> {
  const workbook = await readWorkbookFromFile(file)
  return workbook.SheetNames.filter(name => !DECLARATION_ELEMENTS_SHEET_PATTERN.test(name))
}

// Z42.2.2: reads exactly the 2 sheets staff explicitly assigned (multi_sheet
// structure_type), in canonical invoice-then-packingList order, regardless
// of which physical slot each sheet occupies in the workbook. Used both when
// the wizard builds a template's initial field_mappings, and at replay time
// when a matched template's own sheet_assignments names still exist in a
// fresh upload.
export async function readNamedSheets(file: File, invoiceSheetName: string, plSheetName: string): Promise<{ rows: unknown[][]; sheets: SheetInfo[] }> {
  const workbook = await readWorkbookFromFile(file)
  const rows: unknown[][] = []
  const sheets: SheetInfo[] = []
  for (const [role, sheetName] of [['invoice', invoiceSheetName], ['packingList', plSheetName]] as [SheetRole, string][]) {
    const worksheet = workbook.Sheets[sheetName]
    const sheetRows = worksheet ? (XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][]) : []
    sheets.push({ name: sheetName, role, startRow: rows.length, rowCount: sheetRows.length, fileName: file.name })
    rows.push(...sheetRows)
  }
  return { rows, sheets }
}

// Same idea, for the "2 separate files (CI + PL)" upload path -- each file's
// own relevant sheet(s) are read and appended in file order, so a template
// built from either a single combined workbook or two separate files ends
// up with the exact same concatenated-grid shape.
export async function readMultiFileRows(files: File[], opts?: { raw?: boolean }): Promise<{ rows: unknown[][]; sheets: SheetInfo[] }> {
  const rows: unknown[][] = []
  const sheets: SheetInfo[] = []
  for (const file of files) {
    const part = await readExcelRowsMultiSheet(file, opts)
    for (const sheet of part.sheets) sheets.push({ ...sheet, startRow: sheet.startRow + rows.length })
    rows.push(...part.rows)
  }
  return { rows, sheets }
}

export function buildInvoicePivot(items: InvoiceItem[]): InvoicePivotItem[] {
  const map = new Map<string, InvoicePivotItem>()
  for (const item of items) {
    const key = item.hs_code || 'UNKNOWN'
    if (!map.has(key)) {
      map.set(key, {
        hs_code: key,
        goods_description: item.goods_description || '',
        pcs: 0,
        ctns: 0,
        gross_weight: 0,
        net_weight: 0,
        amount: 0,
        cbm: 0,
      })
    }
    const pivot = map.get(key)!
    pivot.pcs = (pivot.pcs || 0) + (item.pcs || 0)
    pivot.ctns = (pivot.ctns || 0) + (item.ctns || 0)
    pivot.gross_weight = round2((pivot.gross_weight || 0) + (item.gross_weight || 0)) ?? 0
    pivot.net_weight = round2((pivot.net_weight || 0) + (item.net_weight || 0)) ?? 0
    pivot.amount = round2((pivot.amount || 0) + (item.amount || 0)) ?? 0
    pivot.cbm = (pivot.cbm || 0) + (item.cbm || 0)
  }
  return Array.from(map.values())
}

export async function readExcelAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader()

      reader.onload = (e) => {
        try {
          const data = e.target?.result
          if (!data) {
            throw new Error('FileReader returned no data')
          }

          const workbook = XLSX.read(data, { type: 'array' })
          let text = ''

          for (const sheetName of workbook.SheetNames) {
            const worksheet = workbook.Sheets[sheetName]
            if (!worksheet) continue
            text += `Sheet: ${sheetName}\n${XLSX.utils.sheet_to_csv(worksheet)}\n\n`
          }

          if (!text || text.trim().length === 0) {
            throw new Error('Excel file produced empty text - file may be corrupted or missing data')
          }

          resolve(text)
        } catch (err: any) {
          const errMsg = err?.message || err?.error_description || String(err) || 'Unknown error'
          reject(new Error(`Failed to parse Excel: ${errMsg}`))
        }
      }

      reader.onerror = () => reject(new Error('Failed to read file - file may be too large or corrupted'))
      reader.onabort = () => reject(new Error('File reading was aborted'))
      reader.readAsArrayBuffer(file)
    } catch (err: any) {
      const errMsg = err?.message || err?.error_description || String(err) || 'Unknown error'
      reject(new Error(`Failed to read Excel file: ${errMsg}`))
    }
  })
}

// Z67.13: `opts.raw` defaults to false (unchanged) for every other caller of this shared
// function. Passing `{ raw: true }` (only SageBulkImport.tsx does) disables SheetJS's own
// speculative type-guessing when reading CSV text -- confirmed live via direct testing that
// without it, an ambiguous CSV date string like "04/09/2026" gets SILENTLY reinterpreted as US
// MM/DD (September -> "4/9" read as April 9) and converted to an Excel serial number BEFORE our
// own DD/MM/YYYY parsing ever sees it -- this is not a parsing-logic bug downstream, the wrong
// date is already baked in by the time a raw number reaches formatDateCell/parseCsvDate. With
// raw:true, a genuinely ambiguous CSV text cell stays literal text (confirmed empirically); a
// REAL native .xlsx date cell (which has its own already-resolved, unambiguous stored value)
// is unaffected either way, also confirmed empirically.
export async function readExcelRows(file: File, opts?: { raw?: boolean }): Promise<unknown[][]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const data = reader.result as ArrayBuffer
        const workbook = XLSX.read(data, { type: 'array', raw: opts?.raw ?? false })
        const worksheet = workbook.Sheets[workbook.SheetNames[0]]
        resolve(XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][])
      } catch (err) {
        reject(err)
      }
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsArrayBuffer(file)
  })
}

// Row-indexed grid text sent to the AI when no cached template exists for a
// customer. One line per row, JSON-stringified, so the col index the AI
// reports in field_mappings matches rows[r][c] exactly -- unlike CSV text,
// this can't shift columns when a cell contains a comma.
export function buildRowGridText(rows: unknown[][]): string {
  return rows.map((row, i) => `Row ${i}: ${JSON.stringify(row)}`).join('\n')
}

function getExcelFileType(file: File): 'xls' | 'xlsx' {
  return file.name.toLowerCase().endsWith('.xls') ? 'xls' : 'xlsx'
}

// Z42.2.2: the AI-proposal half of runAIFieldMappingSetup, split out so the
// Template Setup Wizard can supply rows read however its chosen structure
// type demands (single sheet only, 2 explicitly-assigned sheets, or 2
// separate files) instead of always going through the auto-detecting
// multi-sheet reader.
export async function runAIFieldMappingSetupFromRows(rows: unknown[][]): Promise<InvoiceParsed> {
  const rowGridText = buildRowGridText(rows)
  return parseInvoiceWithClaude(rowGridText, undefined, 'excel')
}

// Always parses via AI, ignoring any existing template -- used by the
// Template Setup Wizard's edit-mode/legacy path, which needs a fresh AI
// proposal (and the raw rows, to render the preview grid) regardless of
// whether this customer already has a template for a *different* format.
export async function runAIFieldMappingSetup(file: File): Promise<{ rows: unknown[][]; sheets: SheetInfo[]; parsed: InvoiceParsed }> {
  const fileType = getFileType(file)
  if (fileType !== 'excel') {
    throw new Error('Template Setup Wizard only supports Excel sample files (.xlsx, .xls) -- field_mappings is a row/col concept with no PDF equivalent')
  }
  const { rows, sheets } = await readExcelRowsMultiSheet(file)
  const parsed = await runAIFieldMappingSetupFromRows(rows)
  return { rows, sheets, parsed }
}

// customerId is optional: the main CI upload flow always has one (customer
// selection is required before file upload there), but this function is also
// used by the standalone Three-Way Verification upload path
// (src/lib/verification/ciFromUpload.ts), which parses an ad-hoc file with no
// customer/template concept at all -- that path always goes straight to AI.
//
// options resolve an earlier AmbiguousTemplateMatch: forceTemplateId applies
// a specific template staff picked from the modal; forceAI skips template
// matching entirely ("None of these, AI parse it").
export async function parseCommercialInvoiceFile(
  fileOrFiles: File | File[],
  customerId?: string,
  options?: { forceTemplateId?: string; forceAI?: boolean }
): Promise<InvoiceParsed & {
  templateId?: string | null
  templateName?: string | null
  // The template's own field_mappings (when the template path applied) --
  // distinct from InvoiceParsed.field_mappings, which is only populated on a
  // fresh AI parse. Lets the caller merge in a strip_before/strip_after rule
  // and updateTemplate() when the reviewer corrects a labeled-cell value.
  templateFieldMappings?: FieldMappings | null
  raw_header_cells?: Record<string, unknown>
  // How many templates existed for this customer+file-type, regardless of
  // whether one matched -- lets the UI distinguish "no template matched"
  // from "this customer has no templates at all" (same fallback, different
  // banner wording).
  templatesConsidered?: number
  // Template-replay path only: header fields that failed sanity validation
  // (see invoiceFieldValidation.ts) and were overwritten with a targeted
  // single-field AI read -- field name -> the AI's replacement value. Lets
  // the UI offer to save the correction back into the template.
  aiCorrectedFields?: Record<string, unknown>
  // Z42.2: which sheet(s) (of the possibly-2-file, possibly-multi-sheet
  // upload) were actually read, and what role each was detected as -- excel
  // path only (undefined for a PDF upload).
  sheets?: SheetInfo[]
}> {
  const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles]
  const file = files[0]
  const fileType = getFileType(file)
  if (fileType === 'unsupported') {
    throw new Error('Unsupported file type. Please upload a PDF or Excel file (.pdf, .xlsx, .xls)')
  }
  if (files.length > 1 && files.some(f => getFileType(f) !== 'excel')) {
    throw new Error('Uploading a separate CI + Packing List file pair is only supported for Excel files')
  }

  // Hybrid validation: runs on EVERY path (template-replay AND fresh AI
  // parse) before the result is handed back to the UI -- a template's row/col
  // can drift (extra row inserted, supplier tweaked their layout) and
  // silently keep reading the wrong cell, e.g. "Invoice No.*" instead of the
  // value cell beside it. A fresh AI parse can miss the same field for
  // unrelated reasons. Either way, invalidFields is patched with a second,
  // narrowly-targeted AI read instead of shipping obviously-wrong data.
  async function validateAndCorrectHeader(
    header: any,
    getRawTextForCorrection: () => string,
    sourceFormat: 'excel' | 'pdf'
  ): Promise<Record<string, unknown>> {
    const invalidFields = findInvalidHeaderFields(header)
    const aiCorrectedFields: Record<string, unknown> = {}
    if (invalidFields.length === 0) return aiCorrectedFields
    const rawText = getRawTextForCorrection()
    for (const field of invalidFields) {
      try {
        const value = await parseSingleFieldWithClaude(field, rawText, sourceFormat)
        if (value !== null && value !== undefined && value !== '') {
          header[field] = value
          aiCorrectedFields[field] = value
        }
      } catch (err) {
        console.error(`[validateAndCorrectHeader] Single-field AI correction failed for "${field}":`, err)
      }
    }
    return aiCorrectedFields
  }

  if (fileType === 'pdf') {
    const base64 = await fileToBase64(file)
    const { rawText } = await extractTextFromPDF(base64)
    if (!rawText || rawText.trim().length === 0) {
      throw new Error('PDF extraction produced no text - file may be empty or corrupted')
    }
    const result = await parseInvoiceWithClaude(rawText, undefined, 'pdf')
    const aiCorrectedFields = await validateAndCorrectHeader(result.header, () => rawText, 'pdf')
    return { ...result, aiCorrectedFields: Object.keys(aiCorrectedFields).length > 0 ? aiCorrectedFields : undefined }
  }

  const { rows, sheets } = files.length > 1 ? await readMultiFileRows(files) : await readExcelRowsMultiSheet(file)
  const excelFileType = getExcelFileType(file)

  // Z42.2.2: a template built with an explicit structure_type re-reads the
  // fresh upload the same way, rather than trusting whatever the generic
  // auto-detecting reader above already produced -- most importantly for
  // 'single_sheet', where blindly reusing the auto-detected multi-sheet rows
  // could let the item-table loop run on past the Invoice sheet's real end
  // into an unrelated 2nd sheet this template was never built to expect.
  async function readRowsForTemplate(template: InvoiceTemplate): Promise<{ rows: unknown[][]; sheets: SheetInfo[] }> {
    if (template.structure_type === 'single_sheet') {
      return { rows: await readExcelRows(file), sheets: [] }
    }
    if (template.structure_type === 'multi_sheet') {
      const invoiceName = template.sheet_assignments?.invoice?.sheetName
      const plName = template.sheet_assignments?.pl?.sheetName
      if (invoiceName && plName) {
        try {
          const named = await readNamedSheets(file, invoiceName, plName)
          if (named.sheets.every(s => s.rowCount > 0)) return named
        } catch {
          // Sheet names no longer exist in this upload -- fall through to
          // the already-read generic multi-sheet rows below.
        }
      }
    }
    return { rows, sheets }
  }

  async function applyTemplate(template: InvoiceTemplate, templatesConsidered: number) {
    const { rows: templateRows, sheets: templateSheets } = await readRowsForTemplate(template)
    const templateResult = parseUsingTemplate(templateRows, template)
    if (!templateResult) return null

    const aiCorrectedFields = await validateAndCorrectHeader(templateResult.header, () => buildRowGridText(templateRows), 'excel')

    return {
      ...templateResult,
      pivot: buildInvoicePivot(templateResult.items),
      templateId: template.id ?? null,
      templateName: template.name ?? null,
      templateFieldMappings: template.field_mappings ?? null,
      templatesConsidered,
      aiCorrectedFields: Object.keys(aiCorrectedFields).length > 0 ? aiCorrectedFields : undefined,
      sheets: templateSheets,
    }
  }

  if (customerId && !options?.forceAI) {
    if (options?.forceTemplateId) {
      const template = await getTemplateById(options.forceTemplateId)
      if (template) {
        const applied = await applyTemplate(template, 1)
        if (applied) return applied
      }
      // Fall through to AI if the forced template somehow no longer exists
      // or failed to apply -- never silently lose the upload.
    } else {
      // A customer can have more than one template (different suppliers,
      // redesigns, product lines) -- score all of them against this file's
      // actual layout and use whichever one matches, not just "the" template.
      const templates = await getTemplatesForCustomer(customerId, excelFileType)
      // Z42.2.2: a staff-authored signature (shipper text / filename
      // pattern) is simpler and more direct than structure-signature
      // scoring -- try it first, and only fall through to the content
      // fingerprint when it doesn't resolve to exactly one template.
      const signatureMatch = matchTemplateBySignature(templates, rows, files.map(f => f.name))
      if (signatureMatch) {
        const applied = await applyTemplate(signatureMatch, templates.length)
        if (applied) return applied
      }
      const { matched, ambiguous } = matchTemplates(templates, rows, excelFileType)
      if (ambiguous) throw new AmbiguousTemplateMatch(ambiguous)
      if (matched) {
        const applied = await applyTemplate(matched, templates.length)
        if (applied) return applied
      }
      const rowGridText = buildRowGridText(rows)
      const aiResult = await parseInvoiceWithClaude(rowGridText, undefined, 'excel')
      const aiCorrectedFields = await validateAndCorrectHeader(aiResult.header, () => rowGridText, 'excel')
      return { ...aiResult, templatesConsidered: templates.length, aiCorrectedFields: Object.keys(aiCorrectedFields).length > 0 ? aiCorrectedFields : undefined, sheets }
    }
  }

  const rowGridText = buildRowGridText(rows)
  const aiResult = await parseInvoiceWithClaude(rowGridText, undefined, 'excel')
  const aiCorrectedFields = await validateAndCorrectHeader(aiResult.header, () => rowGridText, 'excel')
  return { ...aiResult, aiCorrectedFields: Object.keys(aiCorrectedFields).length > 0 ? aiCorrectedFields : undefined, sheets }
}
